import { useEffect, useState } from 'react'
import { reminderService, ReminderConfig } from '../services/reminderService'
import { ReminderConfigForm } from './ReminderConfigForm'
import { ReminderTestPreview } from './ReminderTestPreview'
import { ReminderLogs } from './ReminderLogs'

const toast = {
  success: (msg: string) => console.log('✅', msg),
  error: (msg: string) => console.log('❌', msg)
}

export function ReminderManagement() {
  const [configs, setConfigs] = useState<ReminderConfig[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingConfig, setEditingConfig] = useState<ReminderConfig | null>(null)
  const [previewingConfig, setPreviewingConfig] = useState<ReminderConfig | null>(null)
  const [activeTab, setActiveTab] = useState<'configs' | 'logs'>('configs')

  useEffect(() => {
    loadConfigs()
  }, [])

  const loadConfigs = async () => {
    setIsLoading(true)
    try {
      const data = await reminderService.getAllReminderConfigs()
      setConfigs(data)
    } catch (error) {
      console.error('Error loading reminder configs:', error)
      toast.error('שגיאה בטעינת התזכורות')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveConfig = async (_config: ReminderConfig) => {
    try {
      await loadConfigs()
    } catch (err) {
      console.error('Error refreshing configs after save:', err)
    } finally {
      setShowForm(false)
      setEditingConfig(null)
    }
  }

  const handleDeleteConfig = async (id: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את התזכורת הזו?')) {
      return
    }

    try {
      await reminderService.deleteReminderConfig(id)
      toast.success('התזכורת נמחקה בהצלחה')
      await loadConfigs()
    } catch (error) {
      console.error('Error deleting config:', error)
      toast.error('שגיאה במחיקת התזכורת')
    }
  }

  const handleToggleConfig = async (config: ReminderConfig) => {
    try {
      await reminderService.toggleReminderConfig(config.id, !config.enabled)
      toast.success(config.enabled ? 'התזכורת הושבתה' : 'התזכורת הופעלה')
      await loadConfigs()
    } catch (error) {
      console.error('Error toggling config:', error)
      toast.error('שגיאה בעדכון התזכורת')
    }
  }

  const getChannelLabel = (channel: string) => {
    switch (channel) {
      case 'email':
        return '📧 דוא"ל'
      case 'whatsapp':
        return '💬 WhatsApp'
      case 'sms':
        return '📱 SMS'
      default:
        return channel
    }
  }

  return (
    <div className="w-full" dir="rtl">
      <div className="bg-gradient-to-l from-primary-600 to-primary-500 text-white px-6 py-8 rounded-lg shadow-lg mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <h1 className="text-3xl font-bold font-hebrew">ניהול תזכורות</h1>
            </div>
            <p className="text-primary-100 font-hebrew">ניהול תזכורות תורנות עבור כל ההורים</p>
          </div>
          {!showForm && (
            <button
              onClick={() => {
                setEditingConfig(null)
                setShowForm(true)
              }}
              className="px-6 py-3 bg-white text-primary-600 rounded-lg font-hebrew font-bold hover:bg-primary-50 hover:shadow-lg active:scale-95 transition-all text-lg shadow-md cursor-pointer"
            >
              ➕ צור תזכורת חדשה
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('configs')}
          className={`px-6 py-2 rounded-lg font-hebrew font-semibold transition-colors flex items-center gap-2 ${
            activeTab === 'configs'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          התזכורות
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-6 py-2 rounded-lg font-hebrew font-semibold transition-colors flex items-center gap-2 ${
            activeTab === 'logs'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          לוגים
        </button>
      </div>

      {showForm && (
        <ReminderConfigForm
          config={editingConfig || undefined}
          onSave={handleSaveConfig}
          onCancel={() => {
            setShowForm(false)
            setEditingConfig(null)
          }}
        />
      )}

      {previewingConfig && (
        <ReminderTestPreview
          config={previewingConfig}
          onClose={() => setPreviewingConfig(null)}
        />
      )}

      {activeTab === 'configs' ? (
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600 font-hebrew">טוען תזכורות...</p>
            </div>
          ) : configs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-500 text-lg font-hebrew mb-4">עדיין אין תזכורות</p>
              <button
                onClick={() => {
                  setEditingConfig(null)
                  setShowForm(true)
                }}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg font-hebrew font-bold hover:bg-primary-700 transition-colors"
              >
                ➕ צור תזכורת ראשונה
              </button>
            </div>
          ) : (
            configs.map((config) => (
              <div
                key={config.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-bold text-gray-900 font-hebrew">
                        {config.name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold font-hebrew ${
                        config.enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {config.enabled ? '✅ פעיל' : '❌ מושבת'}
                      </span>
                    </div>
                    {config.description && (
                      <p className="text-gray-600 mt-1 font-hebrew text-sm">
                        {config.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-sm">
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-gray-600 font-hebrew mb-1">ערוץ</p>
                    <p className="font-bold font-hebrew">{getChannelLabel(config.channel)}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-gray-600 font-hebrew mb-1">זמן הדיוק</p>
                    <p className="font-bold font-hebrew">{config.hours_before} שעות</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-gray-600 font-hebrew mb-1">נוצר</p>
                    <p className="font-bold font-hebrew">
                      {config.created_at
                        ? new Date(config.created_at).toLocaleDateString('he-IL')
                        : '-'}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded p-4 mb-6">
                  <p className="text-sm text-gray-600 font-hebrew mb-2">תצוגה מקדימה:</p>
                  <p className="text-sm text-gray-800 font-hebrew whitespace-pre-wrap line-clamp-3">
                    {config.message_template}
                  </p>
                </div>

                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={() => {
                      setEditingConfig(config)
                      setShowForm(true)
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-hebrew font-semibold text-sm transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    עריכה
                  </button>
                  <button
                    onClick={() => setPreviewingConfig(config)}
                    className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 font-hebrew font-semibold text-sm transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    בדיקה
                  </button>
                  <button
                    onClick={() => handleToggleConfig(config)}
                    className={`px-4 py-2 rounded-lg font-hebrew font-semibold text-sm transition-colors flex items-center gap-2 ${
                      config.enabled
                        ? 'bg-warning-600 text-white hover:bg-warning-700'
                        : 'bg-success-600 text-white hover:bg-success-700'
                    }`}
                  >
                    {config.enabled ? 'השבת' : 'הפעל'}
                  </button>
                  <button
                    onClick={() => handleDeleteConfig(config.id)}
                    className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 font-hebrew font-semibold text-sm transition-colors flex items-center gap-2"
                  >
                    מחיקה
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <ReminderLogs />
      )}
    </div>
  )
}
